import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export function useLectures(onlyFeatured = false) {
  return useQuery({
    queryKey: ['lectures', onlyFeatured],
    queryFn: async () => {
      let query = supabase
        .from('lectures')
        .select('*, lecture_translations(*)')
        .eq('published', true)
        .order('display_order');
      if (onlyFeatured) query = query.eq('featured', true);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

export function useGalleryItems() {
  return useQuery({
    queryKey: ['gallery_items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gallery_items')
        .select('*')
        .eq('published', true)
        .order('display_order');
      if (error) throw error;
      return data;
    },
  });
}

export function usePosts() {
  return useQuery({
    queryKey: ['posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('posts')
        .select('*, post_translations(*)')
        .eq('published', true)
        .order('display_order');
      if (error) throw error;
      return data;
    },
  });
}

// Lightweight version for previews — excludes heavy `content` column to keep homepage payload small.
export function usePostsPreview() {
  return useQuery({
    queryKey: ['posts_preview'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('posts')
        .select('id, slug, image_url, display_order, post_translations(lang, title, excerpt)')
        .eq('published', true)
        .order('display_order');
      if (error) throw error;
      return data;
    },
  });
}

export function useCertificates() {
  return useQuery({
    queryKey: ['certificates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificates')
        .select('*, certificate_translations(*)')
        .eq('published', true)
        .order('display_order');
      if (error) throw error;
      return data;
    },
  });
}

export function useMediaMentions() {
  return useQuery({
    queryKey: ['media_mentions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_mentions')
        .select('*, media_mention_translations(*)')
        .eq('published', true)
        .order('display_order');
      if (error) throw error;
      return data;
    },
  });
}

export function usePartners() {
  return useQuery({
    queryKey: ['partners'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('partners')
        .select('*, partner_translations(*)')
        .eq('published', true)
        .order('display_order');
      if (error) throw error;
      return data;
    },
  });
}

export function useCVEntries() {
  return useQuery({
    queryKey: ['cv_entries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cv_entries')
        .select('*, cv_entry_translations(*)')
        .eq('published', true)
        .order('display_order');
      if (error) throw error;
      return data;
    },
  });
}

export function useGoodDeeds() {
  return useQuery({
    queryKey: ['good_deeds'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('good_deeds')
        .select('*, good_deed_translations(*), good_deed_images(*)')
        .eq('published', true)
        .order('display_order');
      if (error) throw error;
      return data;
    },
  });
}

// Helper to get translation for current language
export function getTranslation<T extends { lang: string }>(
  translations: T[] | undefined,
  lang: string,
  fallbackLang = 'he'
): T | undefined {
  if (!translations?.length) return undefined;
  return translations.find(t => t.lang === lang) || translations.find(t => t.lang === fallbackLang) || translations[0];
}
